import { useSessionRecovery } from '@/stores/sessionRecovery';
import { sessionBlocked, useSessionTransition } from '@/stores/sessionTransition';
import { isCancel } from 'axios';
import { SessionContextCanceledError } from '@/lib/session-cancellation';
import { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';

/**
 * Real-time first: nothing is served from cache silently. Every query is stale at once,
 * refetches on mount and on window focus; sockets patch the cache in between.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        retry: (attempt, error) => attempt < 1 && !isCancel(error) && !sessionBlocked(),
      },
    },
  });
}

/** Clear mounted and inactive queries synchronously when a session is lost. */
export function bindQueryClientToSession(queryClient: QueryClient) {
  const interrupted = new Set<string>();
  let disposed = false;
  let scheduled = false;
  const resumeReads = () => {
    if (scheduled || disposed) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      if (disposed || sessionBlocked() || !useAuthStore.getState().isAuthenticated) return;
      const hashes = new Set(interrupted);
      interrupted.clear();
      void queryClient.refetchQueries({ type: 'active', predicate: (query) =>
        hashes.has(query.queryHash) || query.state.status === 'pending' });
    });
  };
  const unbindCache = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'error' ||
        !(event.action.error instanceof SessionContextCanceledError)) return;
    const query = event.query;
    if (queryClient.getQueryCache().get(query.queryHash) !== query) return;
    interrupted.add(query.queryHash);
    // Query observers batch their React notifications. Restore the cached read
    // synchronously so a context cancellation cannot unmount a filled form.
    query.setState({ error: null, status: query.state.data === undefined ? 'pending' : 'success', fetchStatus: 'idle' });
    if (!sessionBlocked()) resumeReads();
  });
  const unbindAuth = useAuthStore.subscribe((state, previous) => {
    if (previous.isAuthenticated && !state.isAuthenticated) queryClient.clear();
  });
  let blocked = sessionBlocked();
  const onBarrierChange = () => {
    const next = sessionBlocked();
    const previous = blocked;
    blocked = next;
    if (next && !previous) {
      queryClient.getQueryCache().getAll().forEach((query) => {
        if (query.state.fetchStatus !== 'idle') interrupted.add(query.queryHash);
      });
      void queryClient.cancelQueries();
    }
    if (!next && previous) {
      // Resume interrupted reads, including initial loads. Mutations are never replayed.
      // Deferring also lets explicit login/follow finish clearing the old cache.
      resumeReads();
    }
  };
  const unbindRecovery = useSessionRecovery.subscribe(onBarrierChange);
  const unbindTransition = useSessionTransition.subscribe(onBarrierChange);
  return () => { disposed = true; unbindAuth(); unbindRecovery(); unbindTransition(); unbindCache(); };
}
